<template>
  <div class="base-button">
    <component
      :is="buttonTag"
      :type="buttonType"
      v-bind="$attrs"
      :href="href"
      :class="[
        'button',
        {
          'secondary': variant === 'secondary',
          'tertiary': variant === 'text',
          'loading': loading,
          'only-icon': onlyIcon,
          'isDisabled': ($attrs.disabled!==undefined && $attrs.disabled!==false)
        }
      ]"
      @click="() => track()"
    >
      <div
        v-if="!loading"
        :class="`content ${iconPosition}`"
      >
        <div
          v-if="label"
          class="label"
        >
          {{ label }}
        </div>
        <Icon
          v-if="icon"
          :name="icon"
          size="l"
        />
      </div>
      <Spinner
        v-if="loading"
        color_fg="var(--neutral-20)"
        color_bg="var(--white)"
        :size="1"
      />
    </component>
  </div>
</template>


<script lang="ts" setup>
import Spinner from '../Spinner'
import Icon from '../Icon'
import { computed, useAttrs, inject } from 'vue'
import type { ButtonType } from '../../../types/button'
import type { HorizontalPostion } from '../../../types/position'
import type { IconName } from '../../../types/icon'
import { TrackFn } from '../../../types/tracking'

export interface ButtonProps {
  label?: string
  href?: string
  trackingLabel?: string
  variant?: ButtonType
  loading?: boolean
  iconPosition?: HorizontalPostion
  icon?: IconName
  tag?: string | object
  size?: string
}

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()
const {
  label,
  href,
  trackingLabel,
  variant = 'primary',
  loading = false,
  iconPosition = 'right',
  icon = null,
  tag = 'button',
  size = '3rem',
} = defineProps<ButtonProps>()

const Track = inject('track', trackingLabel) as unknown as TrackFn

const track = () => {
  if(trackingLabel){
    Track('button-click', trackingLabel)
  }
}

const onlyIcon = computed(() => icon && !label)

const buttonTag = computed(() => {
  if (href) {
    return 'a'
  }
  return tag
})

const buttonType = computed(() => {
  if (buttonTag.value=='button' || !tag) {
    if (attrs.type) {
      return attrs.type
    } else {
      return 'button'
    }
  }
  return null
})
</script>


<style lang="scss" scoped>
@use '../../../styles' as index;

.base-button {
  display: inline-flex;
}

.content {
  display: flex;
  justify-content: space-between;
  gap: .5rem;
  align-items: center;
}

.content.left {
  flex-flow: row-reverse;
}

.button {
  --size: v-bind('size');
  min-width: 122px;
  padding-inline: 1.75rem;
  border-radius: 50px;
  box-sizing: border-box;
  background-color: index.$color__primaryCoral;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: index.$color__white;
  position: relative;
  font-size: 1rem;
  line-height: 1.3rem;
  min-height: var(--size, 3rem);
  font-family: index.$typo__fontFamily;
  font-weight: index.$typo__fontWeight--bold;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  border-width: 0;
  border-style: none;
  text-decoration: none;
  outline: none;
  word-break: break-word;

  &.only-icon {
    padding: 0;
    justify-content: center;
    width: var(--size, 3rem);
    min-width: 0;
  }

  svg {
    fill: index.$color__white !important;
  }

  &:hover {
    background-color: index.$color__primaryCoral--light;
  }

  &:active {
    background-color: index.$color__primaryCoral--dark;
  }

  &:focus {
    background-color: index.$color__primaryCoral--light;
    box-shadow: inset 0 0 0 4px index.$color__primaryCoral--light, inset 0 0 0 6px index.$color__white;
  }

  &.secondary {
    background-color: index.$color__white;
    box-shadow: none;
    border: 1px solid index.$color__primaryCoral;
    color: index.$color__primaryCoral;

    svg {
      fill: index.$color__primaryCoral !important;
    }

    &:active {
      background-color: index.$color__primaryCoral--light;
      color: index.$color__white;

      svg {
        fill: index.$color__white !important;
      }
    }

    &:focus {
      border-color: transparent;
      box-shadow: inset 0 0 0 4px index.$color__primaryCoral--lightest, inset 0 0 0 6px index.$color__white, inset 0 0 0 7px index.$color__primaryCoral;
    }
  }

  &.tertiary {
    background-color: index.$color__white;
    box-shadow: none;
    color: currentcolor;

    svg {
      fill: currentcolor !important;
    }

    &:is(:focus, :active) {
      box-shadow: inset 0 0 0 5px index.$color__primaryCoral--lightest;
    }
  }
}

.base-button > .button:is(.isDisabled, .loading) {
  background-color: index.$color__neutral--10;
  cursor: default;
  color: index.$color__neutral--40;
  border: none;
}

.base-button > .button:disabled {
  pointer-events: none;
  cursor: not-allowed;
}

.base-button > .button.isDisabled.secondary {
  border: 1px solid index.$color__neutral--20;
}

.base-button > .button.isDisabled.secondary svg {
  fill: index.$color__neutral--40 !important;
}

.base-button > .button:not(.isDisabled).secondary:is(:focus, :hover) {
  background-color: index.$color__primaryCoral--lightest;
}

.base-button > .button.isDisabled.tertiary {
  background-color: index.$color__white;
}

.base-button > .button.isDisabled.tertiary svg {
  fill: index.$color__neutral--40 !important;
}
</style>
